import {action, makeObservable, observable, runInAction} from "mobx";
import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ApplicationInsightsTelemetry} from "../../../utils/telemetry/applicationInsightsTelemetry";
import {Trans} from "react-i18next";
import {ICustomModalButton} from "../../../shared/interfaces/customModalButton";
import {delay} from "lodash";
import {RoutePaths} from "../../../shared/routePaths";
import {ModalWithContentConfig} from "../../../shared/interfaces/showCustomModalConfig";
import {IconSource} from "../../../shared/enums/iconSource";

export interface IModalStore {
  isPrimaryButtonDisabled: boolean;
  isOpen: boolean;
  body: JSX.Element;
  header: JSX.Element;
  headerIcon: string;
  headerIconColor: string;
  primaryButtonText?: string | null;
  primaryButtonIcon?: IconSource;
  primaryButtonTestId?: string | null;
  secondaryButtonText?: string | null;
  secondaryButtonTestId?: string | null;
  centered: boolean;
  hideFooterBackground: boolean;
  footerButtonAlignment: string;
  toggle: () => void;
  openModalWithContent: (config: ModalWithContentConfig) => void;
  openMessageModal: (title: string, message: string) => void;
  /**
   * Open an error modal with custom message. This call is only used when we have an error message ready on frontend.
   */
  openCustomErrorModal: (message: string) => void;
  primaryOnClick: () => void;
  secondaryOnClick: () => void;
  canToggle: boolean;
  dangerButton: boolean;
  /**
   * Open an error modal with pre-defined title and message.
   */
  openErrorModal: (error: Error) => void;
  customButtons?: ICustomModalButton[];
  isFooterVisible?: boolean;
  openSessionExpiredModal: () => void;
}

export class ModalStore implements IModalStore {
  isPrimaryButtonDisabled: boolean;
  isOpen: boolean;
  body: JSX.Element;
  header: JSX.Element;
  headerIcon: string;
  headerIconColor: string;
  primaryButtonText?: string | null;
  primaryButtonIcon?: IconSource;
  primaryButtonTestId?: string | null;
  secondaryButtonText?: string | null;
  secondaryButtonTestId?: string | null;
  centered: boolean;
  hideFooterBackground: boolean;
  footerButtonAlignment: string;
  primaryOnClick: () => void;
  secondaryOnClick: () => void;
  canToggle: boolean;
  dangerButton: boolean;
  customButtons: ICustomModalButton[];
  isFooterVisible: boolean;

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      isPrimaryButtonDisabled: observable,
      body: observable,
      header: observable,
      primaryButtonText: observable,
      primaryButtonIcon: observable,
      secondaryButtonText: observable,
      centered: observable,
      primaryOnClick: observable,
      secondaryOnClick: observable,
      customButtons: observable,
      toggle: action,
      openModalWithContent: action,
      openMessageModal: action,
      openCustomErrorModal: action,
      isFooterVisible: observable
    });
    this.isOpen = false;
    this.isPrimaryButtonDisabled = false;
    this.body = <></>;
    this.header = <></>;
    this.headerIcon = "";
    this.headerIconColor = "";
    this.primaryButtonText = null;
    this.primaryButtonIcon = undefined;
    this.primaryButtonTestId = null;
    this.secondaryButtonText = null;
    this.secondaryButtonTestId = null;
    this.centered = false;
    this.hideFooterBackground = false;
    this.footerButtonAlignment = "end";
    this.primaryOnClick = () => this.toggle();
    this.secondaryOnClick = () => {};
    this.canToggle = true;
    this.dangerButton = false;
    this.customButtons = [];
    this.isFooterVisible = true;
  }

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  openModalWithContent = ({
    content,
    title,
    primaryButtonText = null,
    primaryButtonIcon = undefined,
    primaryButtonTestId = null,
    secondaryButtonText = null,
    secondaryButtonTestId = null,
    centered = false,
    canToggle = true,
    dangerButton = false,
    onPrimaryClick,
    onSecondaryClick,
    customButtons,
    isFooterVisible = true,
    hideFooterBackground = false,
    footerButtonAlignment = "end"
  }: ModalWithContentConfig) => {
    this.resetButtons();
    this.isOpen = false;
    this.body = <>{content}</>;
    this.header = <>{title}</>;
    this.primaryButtonText = primaryButtonText;
    this.primaryButtonIcon = primaryButtonIcon;
    this.primaryButtonTestId = primaryButtonTestId;
    this.secondaryButtonText = secondaryButtonText;
    this.secondaryButtonTestId = secondaryButtonTestId;
    this.centered = centered;
    this.isFooterVisible = isFooterVisible;
    this.hideFooterBackground = hideFooterBackground;
    this.footerButtonAlignment = footerButtonAlignment;
    this.isOpen = true;
    this.canToggle = canToggle;
    this.dangerButton = dangerButton;
    if (onPrimaryClick !== null && onPrimaryClick !== undefined) {
      this.primaryOnClick = onPrimaryClick;
    }
    if (onSecondaryClick !== null && onSecondaryClick !== undefined) {
      this.secondaryOnClick = onSecondaryClick;
    }
    this.customButtons = customButtons ?? [];
  };

  openMessageModal = (title: string, message: string) => {
    this.resetButtons();
    this.openModalWithContent({
      content: <div>{message}</div>,
      title: (
        <div>
          <FontAwesomeIcon color="grey" icon="info" />
          <span className="modalTitle">{title}</span>
        </div>
      )
    });
  };

  resetButtons = () => {
    runInAction(() => {
      this.primaryOnClick = () => this.toggle();
      this.secondaryOnClick = () => {};
    });
  };

  // Now we don't trust error messages coming back from backend.
  // This call is only used when we have an error message ready on frontend
  openCustomErrorModal = (message: string) => {
    this.resetButtons();
    this.openModalWithContent({
      content: <div>{message}</div>,
      title: (
        <div>
          <FontAwesomeIcon color="red" icon="exclamation-triangle" />
          <span className="modalTitle">
            <Trans i18nKey="titles.error" />
          </span>
        </div>
      )
    });
  };

  //still supply error for logging
  openErrorModal = (error: Error) => {
    this.openGenericErrorModal(error);
  };

  private openGenericErrorModal = (error: Error) => {
    this.resetButtons();
    this.openModalWithContent({
      content: (
        <div data-testid="generic-error">
          <Trans i18nKey="defaultModalErrorMessage" />
        </div>
      ),
      title: (
        <div>
          <FontAwesomeIcon color="red" icon="exclamation-triangle" />
          <span className="modalTitle">
            <Trans i18nKey="titles.error" />
          </span>
        </div>
      )
    });
    ApplicationInsightsTelemetry.getInstance().applicationInsights?.trackException(
      {
        error: error
      }
    );
  };

  public openSessionExpiredModal = () => {
    this.openModalWithContent({
      content: (
        <div>
          <Trans i18nKey="sessionExpiredMessage">
            You will be automatically redirected in a few seconds.
            <br />
            If this doesn't happen, <a href={RoutePaths.SignIn}>click there</a>.
          </Trans>
        </div>
      ),
      title: (
        <div>
          <span>
            <Trans i18nKey="titles.sessionExpired" />
          </span>
        </div>
      ),
      isFooterVisible: false
    });
    delay(() => {
      window.location.href = RoutePaths.SignIn;
    }, 5000);
  };
}
